/**
 * React 18 Compatibility Fix
 *
 * Goal: Ensure proper use of React 18's createRoot API instead of deprecated ReactDOM.render
 * This script intercepts calls to ReactDOM.render and redirects them to createRoot
 * following the project's debug-first approach and idempotent pattern.
 */

import { PipefyLogger } from './utils/logger.js';

(function () {
  'use strict';

  class React18Fix {
    constructor() {
      this.config = window.pipefyReact18Config || {};
      this.startedAt = Date.now();
      this.reactDetected = false;
      this.fixApplied = false;
      this.pollers = [];
      this.logger = PipefyLogger.create('React18Fix');
      this.init();
    }

    init() {
      this.logger.info('init: starting React18Fix');

      const onReady = () => {
        this.logger.debug('dom: ready');
        this.detectReact18();
        this.applyFix();
        this.logger.info('init: completed');
      };

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', onReady);
      } else {
        onReady();
      }
    }

    // ---------- React Detection ----------
    detectReact18() {
      this.logger.debug('detect: checking React availability');

      const checkReact = () => {
        if (typeof window.ReactDOM !== 'undefined') {
          if (window.ReactDOM.createRoot) {
            this.reactDetected = true;
            this.logger.info('detect: React 18 detected - createRoot available');
            return true;
          } else {
            this.logger.warn('detect: React detected but createRoot not available');
            return false;
          }
        }
        return false;
      };

      // Check immediately
      if (checkReact()) {
        return;
      }

      // Poll for React availability
      this.logger.debug('detect: React not ready, polling...');
      const poller = setInterval(() => {
        if (checkReact()) {
          clearInterval(poller);
          this.pollers = this.pollers.filter(p => p !== poller);
          this.applyFix();
        }
      }, 100);

      this.pollers.push(poller);

      // Stop polling after 5 seconds
      setTimeout(() => {
        clearInterval(poller);
        this.pollers = this.pollers.filter(p => p !== poller);
        if (!this.reactDetected) {
          this.logger.warn('detect: React 18 not detected after timeout');
        }
      }, 5000);
    }

    // ---------- Fix Application ----------
    applyFix() {
      if (this.fixApplied || !this.reactDetected) {
        return;
      }

      this.logger.debug('fix: applying React 18 compatibility fix');

      try {
        const originalRender = window.ReactDOM.render;

        if (originalRender) {
          window.ReactDOM.render = (element, container) => {
            this.logger.warn('fix: ReactDOM.render blocked - redirecting to createRoot');

            try {
              const root = window.ReactDOM.createRoot(container);
              root.render(element);
              this.logger.debug('fix: successfully redirected to createRoot');
              return root;
            } catch (error) {
              this.logger.error('fix: createRoot failed, falling back to original render', error);
              return originalRender.call(this, element, container);
            }
          };

          this.fixApplied = true;
          this.logger.info('fix: React 18 compatibility fix applied successfully');
        } else {
          this.logger.debug('fix: ReactDOM.render not found, no fix needed');
        }
      } catch (error) {
        this.logger.error('fix: failed to apply React 18 fix', error);
      }
    }

    // ---------- Cleanup ----------
    cleanup() {
      this.logger.debug('cleanup: stopping pollers');
      this.pollers.forEach(poller => clearInterval(poller));
      this.pollers = [];
    }

    // ---------- Status ----------
    getStatus() {
      return {
        startedAt: this.startedAt,
        reactDetected: this.reactDetected,
        fixApplied: this.fixApplied,
        debugMode: this.config.debug,
        pollersActive: this.pollers.length,
      };
    }
  }

  // Initialize the React 18 Fix
  const react18Fix = new React18Fix();

  // Expose to global scope for external access
  window.PipefyReact18Fix = react18Fix;

  // Add global debug commands
  window.pipefyDebug = window.pipefyDebug || {};
  window.pipefyDebug.react18 = {
    status: () => react18Fix.getStatus(),
    cleanup: () => react18Fix.cleanup(),
    help: () => {
      console.group('🛠️ React 18 Fix Debug Commands');
      console.log('pipefyDebug.react18.status() - Show current status');
      console.log('pipefyDebug.react18.cleanup() - Cleanup pollers');
      console.log('pipefyDebug.react18.help() - Show this help');
      console.groupEnd();
    },
  };

  // Auto-show help in debug mode
  if (react18Fix.config.debug) {
    console.log('🔧 React 18 Fix Debug Mode Active - Use pipefyDebug.react18.help() for commands');
  }
})();
